import { useCallback, useEffect, useState } from 'react';
import moment from 'moment';
import Lightbox from 'react-18-image-lightbox';
import { toast } from 'sonner';
import { ChevronDown, Images, Loader2, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { goToCustomerConversation } from '@/utils/goToCustomerConversation';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import usePersistentOpen from '@/hooks/usePersistentOpen';
import {
  CUSTOMER_PHOTOS_CHANGED,
  getCustomerPhotos,
  removeCustomerPhoto,
} from '@/actions/customerGallery';

type CustomerPhoto = {
  _id: string;
  url: string;
  name?: string;
  createdAt: string;
  savedBy?: string | null;
  postedBy?: string | null;
};

type Props = {
  _customer: string;
  _business?: string;
  customerName?: string;
  // 'card' on the customer page, 'section' in the conversation details panel
  variant?: 'card' | 'section';
};

const savedLine = (photo: CustomerPhoto) =>
  [
    photo.postedBy && `From ${photo.postedBy}`,
    photo.savedBy && photo.savedBy !== photo.postedBy && `saved by ${photo.savedBy}`,
    moment(photo.createdAt).format('ll'),
  ]
    .filter(Boolean)
    .join(' · ');

/**
 * Photos saved to a customer from team chat, for anything worth keeping with
 * the customer: job-site photos, before-and-after shots, paperwork.
 */
export function CustomerPhotos({ _customer, _business, customerName, variant = 'card' }: Props) {
  const [photos, setPhotos] = useState<CustomerPhoto[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [photoToRemove, setPhotoToRemove] = useState<CustomerPhoto | null>(null);
  const [isOpen, setIsOpen] = usePersistentOpen('conversation-details:customer-photos');
  const [isOpeningConversation, setIsOpeningConversation] = useState(false);
  const navigate = useNavigate();

  // the photos the customer sent are in their conversation
  const openConversation = async () => {
    setIsOpeningConversation(true);
    try {
      await goToCustomerConversation({ customerId: _customer, navigate });
    } finally {
      setIsOpeningConversation(false);
    }
  };

  const load = useCallback(
    async (before?: string) => {
      setIsLoading(true);
      try {
        const data = await getCustomerPhotos({ _customer, _business, before });
        // an aborted request can come back without a list
        if (data?.error || !Array.isArray(data?.photos)) throw new Error('Could not load photos');
        setPhotos((current) => (before ? [...current, ...data.photos] : data.photos));
        setHasMore(Boolean(data.hasMore));
        setFailed(false);
      } catch {
        setFailed(true);
      } finally {
        setIsLoading(false);
      }
    },
    [_customer, _business],
  );

  useEffect(() => {
    load();
    // photos saved from team chat while this is open
    const onChanged = (event: Event) => {
      const changed = (event as CustomEvent).detail?._customer;
      if (!changed || changed === _customer) load();
    };
    window.addEventListener(CUSTOMER_PHOTOS_CHANGED, onChanged);
    return () => window.removeEventListener(CUSTOMER_PHOTOS_CHANGED, onChanged);
  }, [load, _customer]);

  const remove = async () => {
    if (!photoToRemove) return;
    const removed = photoToRemove;
    setPhotoToRemove(null);
    setOpenIndex(null);
    const data = await removeCustomerPhoto({ _id: removed._id }).catch(() => ({ error: true }));
    if (data.error) {
      toast.error('Could not remove the photo.');
      return;
    }
    setPhotos((current) => current.filter((photo) => photo._id !== removed._id));
  };

  const name = customerName || 'this customer';
  const current = openIndex === null ? null : photos[openIndex];

  const body = (
    <>
      {current && (
        <Lightbox
          mainSrc={current.url}
          nextSrc={photos.length > 1 ? photos[(openIndex! + 1) % photos.length].url : undefined}
          prevSrc={
            photos.length > 1
              ? photos[(openIndex! + photos.length - 1) % photos.length].url
              : undefined
          }
          imageTitle={savedLine(current)}
          toolbarButtons={[
            <button
              key="remove"
              type="button"
              className="px-3 text-sm text-white hover:underline"
              onClick={() => setPhotoToRemove(current)}
            >
              Remove
            </button>,
          ]}
          onCloseRequest={() => setOpenIndex(null)}
          reactModalStyle={{ zIndex: 99999 }}
          onMovePrevRequest={() => setOpenIndex((openIndex! + photos.length - 1) % photos.length)}
          onMoveNextRequest={() => setOpenIndex((openIndex! + 1) % photos.length)}
        />
      )}

      {isLoading && !photos.length ? (
        <div className="flex justify-center py-4">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : failed && !photos.length ? (
        <p className="text-sm text-muted-foreground">
          Photos could not be loaded.{' '}
          <button type="button" className="underline" onClick={() => load()}>
            Try again
          </button>
        </p>
      ) : !photos.length ? (
        <div className="space-y-1 text-sm text-muted-foreground">
          <p className="font-medium text-foreground">No team photos yet</p>
          <p>
            Photos sent in team chat with {name} linked are saved here automatically, like
            job-site photos, before-and-after shots or paperwork.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          <div className={variant === 'card' ? 'grid grid-cols-4 gap-2 max-md:grid-cols-3' : 'grid grid-cols-2 gap-2'}>
            {photos.map((photo, index) => (
              <div key={photo._id} className="group relative aspect-square overflow-hidden rounded-lg bg-muted">
                <button
                  type="button"
                  className="h-full w-full"
                  title={savedLine(photo)}
                  onClick={() => setOpenIndex(index)}
                >
                  <img
                    src={photo.url}
                    alt={photo.name || `Photo ${index + 1}`}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform group-hover:scale-105"
                  />
                </button>
                <button
                  type="button"
                  aria-label="Remove photo"
                  className="absolute right-1 top-1 hidden rounded-full bg-background/90 p-1 text-muted-foreground shadow hover:text-destructive group-hover:block touch:block touch:p-2 touch:before:absolute touch:before:-inset-1.5"
                  onClick={() => setPhotoToRemove(photo)}
                >
                  <Trash2 className="size-3.5" />
                </button>
              </div>
            ))}
          </div>
          {hasMore && (
            <Button
              variant="ghost"
              size="sm"
              className="w-full"
              disabled={isLoading}
              onClick={() => load(photos[photos.length - 1].createdAt)}
            >
              {isLoading ? 'Loading…' : 'Show more'}
            </Button>
          )}
        </div>
      )}

      <AlertDialog open={!!photoToRemove} onOpenChange={(open) => !open && setPhotoToRemove(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this photo?</AlertDialogTitle>
            <AlertDialogDescription>
              It is removed from {name}&apos;s team photos for everyone. The team chat message it came
              from is not changed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove}>Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );

  if (variant === 'section') {
    return (
      <div className="border-b border-border/60">
        <Collapsible open={isOpen} onOpenChange={setIsOpen}>
          {/* Phones: the whole row toggles the section with the chevron on
              the right, like the other details sections (DetailsSectionHeader). */}
          <div className="relative flex items-center gap-2 px-4 py-3 max-md:min-h-12 max-md:py-1 max-md:pr-11">
            <CollapsibleTrigger asChild>
              <button
                type="button"
                aria-label="Team photos"
                className="group h-5 w-5 text-muted-foreground transition-transform touch:md:relative touch:md:before:absolute touch:md:before:-inset-3 max-md:absolute max-md:inset-0 max-md:flex max-md:h-auto max-md:w-auto max-md:items-center max-md:justify-end max-md:pr-3 max-md:transition-colors max-md:focus-visible:outline-none max-md:focus-visible:ring-2 max-md:focus-visible:ring-inset max-md:focus-visible:ring-ring max-md:active:bg-muted/60"
              >
                <ChevronDown className="h-4 w-4 transition-transform duration-200 group-data-[state=closed]:-rotate-90 max-md:size-5 max-md:text-muted-foreground/60" />
              </button>
            </CollapsibleTrigger>
            <h3 className="text-sm font-medium max-md:pointer-events-none max-md:relative max-md:truncate max-md:text-base">
              Team photos
            </h3>
            {photos.length > 0 && (
              <span className="text-xs text-muted-foreground max-md:pointer-events-none max-md:relative max-md:text-sm">
                {photos.length}
                {hasMore ? '+' : ''}
              </span>
            )}
          </div>
          <CollapsibleContent>
            <div className="px-4 pb-3">
              {/* the conversation's own pictures are in the Images section */}
              <p className="mb-2 text-xs leading-5 text-muted-foreground">
                Saved by your team from team chat. Photos {name} sends are under Images.
              </p>
              {body}
            </div>
          </CollapsibleContent>
        </Collapsible>
      </div>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0 pb-3">
        <div className="min-w-0 space-y-1">
          <CardTitle className="flex items-center gap-2 text-xl font-semibold">
            <Images size={20} />
            <span>Team photos</span>
          </CardTitle>
          <CardDescription>
            Photos your team saved from team chat. Photos {name} sends you stay in their
            conversation.
          </CardDescription>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="shrink-0 rounded-lg shadow-none"
          disabled={isOpeningConversation}
          onClick={openConversation}
        >
          {isOpeningConversation && <Loader2 className="mr-1.5 size-3.5 animate-spin" />}
          Open conversation
        </Button>
      </CardHeader>
      <CardContent>{body}</CardContent>
    </Card>
  );
}
